import { Test, TestQuestion, TestSession, TestResult } from '../types/test';
import api from '../lib/api';


class TestService {
  private currentSession: TestSession | null = null;

  // Get all tests for the student
  async getStudentTests(): Promise<Test[]> {
    const response = await api.get('/student/tests');

    // Transform the response to match our frontend Test interface
    const tests = response.data.map((test: any) => ({
      id: test.id,
      title: test.title,
      subject: test.subject,
      description: test.description,
      duration: test.duration,
      totalQuestions: test.totalQuestions,
      scheduledStartTime: test.scheduledStartTime,
      scheduledEndTime: test.scheduledEndTime,
      allowedStartTime: test.allowedStartTime,
      instructions: test.instructions ? test.instructions.split('\n') : [],
      status: test.status,
      canStart: test.canStart,
      canViewInstructions: test.canViewInstructions,
      canStartTest: test.canStartTest,
      canResume: test.canResume,
      hasStarted: test.hasStarted,
      hasCompleted: test.hasCompleted,
      assignmentId: test.assignmentId,
      maxAttempts: test.maxAttempts
    }));

    return tests;
  }

  // Get upcoming tests (exclude completed and expired)
  async getUpcomingTests(): Promise<Test[]> {
    const tests = await this.getStudentTests();
    return tests.filter(test => {
      if (test.status === 'completed' || test.status === 'expired' || test.status === 'missed') {
        return false;
      }
      // Exclude tests whose schedule has passed
      if (test.scheduledEndTime && this.isTestScheduleExpired(test)) {
        return false;
      }
      return test.status === 'upcoming' || 
             test.status === 'scheduled' || 
             test.status === 'in-progress';
    });
  }

  // Get completed tests
  async getCompletedTests(): Promise<Test[]> {
    const tests = await this.getStudentTests();
    return tests.filter(test => test.status === 'completed');
  }

  // Get schedule-expired tests (separate category)
  async getExpiredTests(): Promise<Test[]> {
    const tests = await this.getStudentTests();
    return tests.filter(test => {
      return (test.status === 'upcoming' || test.status === 'in-progress') &&
             this.isTestScheduleExpired(test);
    });
  }

  // Parse server date; treat naive timestamps as local to avoid unintended UTC shifts
  private parseServerDate(dateString: string): Date {
    return new Date(dateString);
  }

  // Check if test schedule has expired (cannot be started anymore)
  isTestScheduleExpired(test: Test): boolean {
    const now = new Date();
    const scheduleEndTime = this.parseServerDate(test.scheduledEndTime);
    return now > scheduleEndTime;
  }

  // Check if an active test session should be auto-completed
  async isTestSessionExpired(testId: string): Promise<boolean> {
    try {
      const timerData = await this.syncTimer(testId);
      return timerData.isExpired || timerData.timeRemaining <= 0;
    } catch (error) {
      console.error('Failed to check test session expiry:', error);
      return false;
    }
  }

  // Get test by ID
  async getTestById(testId: string): Promise<Test | null> {
    const response = await api.get(`/student/tests/${testId}`);
    const test = response.data;

    return {
      id: test.id,
      title: test.title,
      subject: test.subject,
      description: test.description,
      duration: test.duration,
      totalQuestions: test.totalQuestions,
      scheduledStartTime: test.scheduledStartTime,
      scheduledEndTime: test.scheduledEndTime,
      allowedStartTime: test.allowedStartTime,
      instructions: test.instructions ? test.instructions.split('\n') : [],
      status: test.status,
      canStart: test.canStart,
      canViewInstructions: test.canViewInstructions,
      canStartTest: test.canStartTest,
      canResume: test.canResume,
      hasStarted: test.hasStarted,
      hasCompleted: test.hasCompleted,
      assignmentId: test.assignmentId,
      maxAttempts: test.maxAttempts,
      // Assignment information
      assignmentType: test.assignmentType,
      assignedClassNames: test.assignedClassNames,
      assignedToSelectedStudents: test.assignedToSelectedStudents
    };
  }

  // Start a test
  async startTest(testId: string): Promise<TestSession> {
    // Start test via API
    const response = await api.post(`/student/tests/${testId}/start`);
    this.currentSession = response.data;
    if (!this.currentSession) {
      throw new Error('Invalid session data from API');
    }

    // Ensure answers and markedForReview are initialized
    if (!this.currentSession.answers) {
      this.currentSession.answers = {};
    }
    if (!this.currentSession.markedForReview) {
      this.currentSession.markedForReview = [];
    }

    return this.currentSession;
  }

  // Get current test session
  getCurrentSession(): TestSession | null {
    return this.currentSession;
  }

  // Attempt to resume an existing test session from server
  async resumeTest(testId: string): Promise<TestSession | null> {
    try {
      const response = await api.get(`/student/tests/${testId}/session`);
      const session = response.data as TestSession;
      if (session && !session.isSubmitted) {
        this.currentSession = session;
        // Ensure structures are initialized
        if (!this.currentSession.answers) this.currentSession.answers = {};
        if (!this.currentSession.markedForReview) this.currentSession.markedForReview = [];
        return this.currentSession;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  // Get test questions
  async getTestQuestions(testId: string): Promise<TestQuestion[]> {
    const response = await api.get(`/student/tests/${testId}/questions`);

    // Transform backend Question objects to frontend TestQuestion interface
    const questions = response.data.map((question: any, index: number) => ({
      id: question.id,
      questionNumber: index + 1,
      question: question.questionText, // Legacy field
      questionText: question.questionText,
      questionImageUrl: question.questionImageUrl,
      questionType: question.type, // Map backend 'type' to frontend 'questionType'
      subject: question.subject,
      topic: question.topic,
      options: question.options || [],
      correctAnswer: question.correctAnswer,
      explanation: question.explanation,
      explanationImageUrl: question.explanationImageUrl,
      isAnswered: false,
      isMarkedForReview: false,
      timeSpent: 0
    }));

    return questions;
  }

  // Save answer
  async saveAnswer(questionId: string, selectedOption: number | string | number[] | null): Promise<void> {
    if (!this.currentSession) {
      throw new Error('No active test session');
    }

    // Update local session - ensure answers object exists
    if (!this.currentSession.answers) {
      this.currentSession.answers = {};
    }

    // Handle clearing response (null value)
    if (selectedOption === null || selectedOption === undefined || selectedOption === '') {
      delete this.currentSession.answers[questionId];

      // Clear answer via API
      await api.delete(`/student/tests/${this.currentSession.testId}/answers/${questionId}`);
    } else {
      // Store the answer in local session (keep original format for local state)
      this.currentSession.answers[questionId] = selectedOption as any;

      // Convert answer to backend format for API call
      let backendAnswer: string;
      if (Array.isArray(selectedOption)) {
        // For MCQ_MULTIPLE_CORRECT: convert array of indices to comma-separated option letters
        const optionLetters = selectedOption.map(index => String.fromCharCode(65 + index)); // 0->A, 1->B, etc.
        backendAnswer = optionLetters.join(',');
        console.log(`Converting MCQ_MULTIPLE answer for question ${questionId}: indices [${selectedOption}] -> letters "${backendAnswer}"`);
      } else if (typeof selectedOption === 'number') {
        // For MCQ_SINGLE_CORRECT and TRUE_FALSE: convert index to option letter
        backendAnswer = String.fromCharCode(65 + selectedOption); // 0->A, 1->B, etc.
        console.log(`Converting MCQ/TRUE_FALSE answer for question ${questionId}: index ${selectedOption} -> letter "${backendAnswer}"`);
      } else {
        // For text/numeric answers: use as-is
        backendAnswer = selectedOption.toString();
        console.log(`Using text/numeric answer for question ${questionId}: "${backendAnswer}"`);
      }

      // Save answer via API
      await api.post(`/student/tests/${this.currentSession.testId}/answers`, {
        questionId,
        selectedOption: backendAnswer
      });
    }
  }

  // Mark question for review
  async markForReview(questionId: string, mark: boolean): Promise<void> {
    if (!this.currentSession) {
      throw new Error('No active test session');
    }

    // Mark for review via API
    await api.post(`/student/tests/${this.currentSession.testId}/mark-review`, {
      questionId,
      mark
    });

    // Update local session - ensure markedForReview array exists
    if (!this.currentSession.markedForReview) {
      this.currentSession.markedForReview = [];
    }

    if (mark) {
      if (!this.currentSession.markedForReview.includes(questionId)) {
        this.currentSession.markedForReview.push(questionId);
      }
    } else {
      this.currentSession.markedForReview = this.currentSession.markedForReview.filter(
        id => id !== questionId
      );
    }
  }

  // Submit test
  async submitTest(timeTrackingData?: {
    questionTimeSpent?: Record<string, number>;
    totalTimeSpent?: number;
  }, behaviorAnalytics?: any): Promise<TestResult> {
    if (!this.currentSession) {
      throw new Error('No active test session');
    }

    // Calculate time spent (start time to now)
    const startTime = new Date(this.currentSession.startTime);
    const currentTime = new Date();
    const timeSpentSeconds = Math.floor((currentTime.getTime() - startTime.getTime()) / 1000);

    // Convert answers to proper format for backend
    const answersAsStrings: Record<string, string> = {};
    if (this.currentSession.answers) {
      Object.entries(this.currentSession.answers).forEach(([questionId, selectedOption]) => {
        if (selectedOption !== null && selectedOption !== undefined) {
          // Handle different answer types
          if (Array.isArray(selectedOption)) {
            // For MCQ_MULTIPLE_CORRECT: convert array of 0-based indices to 1-based numbers
            const oneBasedIndices = selectedOption.map(index => index + 1); // 0->1, 1->2, 2->3, etc.
            answersAsStrings[questionId] = oneBasedIndices.join(',');
            console.log(`Submit: Converting MCQ_MULTIPLE answer for question ${questionId}: 0-based indices [${selectedOption}] -> 1-based numbers "${answersAsStrings[questionId]}"`);
          } else if (typeof selectedOption === 'number') {
            // For MCQ_SINGLE_CORRECT and TRUE_FALSE: convert 0-based index to 1-based number
            answersAsStrings[questionId] = String(selectedOption + 1); // 0->1, 1->2, 2->3, etc.
            console.log(`Submit: Converting MCQ/TRUE_FALSE answer for question ${questionId}: 0-based index ${selectedOption} -> 1-based number "${answersAsStrings[questionId]}"`);
          } else {
            // For text/numeric answers: use as-is
            answersAsStrings[questionId] = String(selectedOption);
            console.log(`Submit: Using text/numeric answer for question ${questionId}: "${answersAsStrings[questionId]}"`);
          }
        }
      });
    }

    // Prepare submission data
    const submissionData: any = {
      answers: answersAsStrings,
      markedForReview: this.currentSession.markedForReview || [],
      timeSpent: timeSpentSeconds,
      isAutoSubmitted: false
    };

    // Add time tracking data if provided
    if (timeTrackingData) {
      submissionData.questionTimeSpent = timeTrackingData.questionTimeSpent;
      submissionData.totalTimeSpent = Math.floor((timeTrackingData.totalTimeSpent || 0) / 1000); // Convert to seconds
    }

    // Add behavior analytics if provided
    if (behaviorAnalytics) {
      submissionData.behaviorAnalytics = behaviorAnalytics;
    }

    // Submit test via API
    const response = await api.post(`/student/tests/${this.currentSession.testId}/submit`, submissionData);

    this.currentSession = null;
    return response.data;
  }

  // Add warning (tab switch, etc.)
  addWarning(): void {
    if (this.currentSession) {
      this.currentSession.warningCount++;
    }
  }

  // Update time remaining
  updateTimeRemaining(seconds: number): void {
    if (this.currentSession) {
      this.currentSession.timeRemaining = seconds;
    }
  }

  // Sync timer with server
  async syncTimer(testId: string): Promise<{
    timeRemaining: number;
    isExpired: boolean;
    forceCompletion?: boolean;
    forceSubmission?: boolean;
    message?: string;
  }> {
    const response = await api.get(`/student/tests/${testId}/timer`);
    return {
      timeRemaining: response.data.timeRemaining,
      isExpired: response.data.isExpired || response.data.expired, // Handle both field names for compatibility
      forceCompletion: response.data.forceCompletion,
      forceSubmission: response.data.forceSubmission,
      message: response.data.message
    };
  }
}

export const testService = new TestService();
export default testService;
